import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LucideAngularModule } from 'lucide-angular';
import { SpinnerComponent } from '../../../components/spinner/spinner.component';
import { Icons } from '../../../../shared/helpers/icons';
import { IContact } from '../../../../shared/models/contact';
import { UserDataService } from '../../../../shared/services/userData.service';
import { ContactsService } from '../../../../shared/services/contact.service';
import { ISpecialNeedsStaff } from '../../../../shared/models/specialNeeds';
import { SpecialNeedsService } from '../../../../shared/services/specialNeeds.service';

@Component({
  selector: 'special-needs-staff-form',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule, SpinnerComponent],
  templateUrl: './staff-form.component.html'
})
export class StaffFormComponent implements OnInit {

  icons = Icons;

  @Input() itemToEdit: ISpecialNeedsStaff | null = null;

  @Output() onClose = new EventEmitter<void>();
  @Output() onSave = new EventEmitter<{ type: string, message: string }>();

  currentBuildingId: string | null = null;
  staff: ISpecialNeedsStaff = {};
  contactsList: IContact[] = [];
  loading: boolean = false;

  constructor(
    private userDataService: UserDataService,
    private contactsService: ContactsService,
    private specialNeedsService: SpecialNeedsService
  ) {}

  ngOnInit(): void {
    this.currentBuildingId = this.userDataService.getBuildingId();
    this.staff = this.itemToEdit ? { ...this.itemToEdit } : {};
    this.loadContacts();
  }

  loadContacts(): void {
    if (!this.currentBuildingId) return;
    this.loading = true;
    this.contactsService.getContacts(this.currentBuildingId).subscribe({
      next: (response) => {
        this.loading = false;
        this.contactsList = (response.data || []).sort((a, b) => a.name.localeCompare(b.name));
      },
      error: () => { this.loading = false; }
    });
  }

  get isEdit(): boolean {
    return !!this.itemToEdit?.id;
  }

  get isValid(): boolean {
    return !!this.staff.staff_name;
  }

  closeModal(): void {
    this.onClose.emit();
  }

  save(): void {
    if (!this.currentBuildingId || !this.isValid) return;

    const payload: Partial<ISpecialNeedsStaff> = {
      staff_name: this.staff.staff_name,
      primary_work_location: this.staff.primary_work_location,
      type_of_assistance: this.staff.type_of_assistance,
    };

    this.loading = true;
    const request$ = this.isEdit
      ? this.specialNeedsService.updateStaff(this.currentBuildingId, String(this.itemToEdit!.id), payload)
      : this.specialNeedsService.createStaff(this.currentBuildingId, payload);

    request$.subscribe({
      next: (response) => {
        this.loading = false;
        if (!response.has_error) {
          this.onSave.emit({ type: 'success', message: this.isEdit ? 'This occurred after updating a staff member.' : 'This occurred after adding a staff member.' });
        } else {
          this.onSave.emit({ type: 'unsuccess', message: response.message });
        }
      },
      error: (err) => {
        this.loading = false;
        this.onSave.emit({ type: 'unsuccess', message: err.message });
      }
    });
  }
}
